import { http } from '../api/http';
import { ENV } from '../config/env';

export type DeveloperProjectReport = {
  projectId: string;
  projectName?: string;
  projectStatus?: string;
  projectState?: string;
  isActive?: boolean;
  fundedAmount: number;
  blocksSold: number;
  blocksTotal: number;
  fundedPct: number;
  investorsCount: number;
  fundingTimeline?: { date: string; amount: number }[];
  projectedCloseDate?: string | null;
  projectionNote?: string;
  investments?: {
    orderId: string;
    userId: string;
    userName: string;
    userEmail?: string;
    amount: number;
    currency: string;
    investedAt: string;
    paymentMethod: 'PAYPAL' | 'TRANSFER_PROOF' | 'UNKNOWN';
    paypalOrderId?: string | null;
    paypalCaptureId?: string | null;
    transferProofUrl?: string | null;
  }[];
  closures?: {
    orderId: string;
    userId: string;
    userName: string;
    userEmail?: string;
    capital: number;
    yield: number;
    total: number;
    currency: string;
    closedAt: string;
  }[];
  closureSummary?: {
    totalCapital: number;
    totalYield: number;
    totalReturned: number;
    ordersClosed: number;
  };
};

function getToken() {
  return localStorage.getItem('auth_token');
}

function deriveFilenameFromHeaders(headers: Headers, fallback: string) {
  const cd = headers.get('content-disposition') || headers.get('Content-Disposition');
  const match = cd?.match(/filename\*?=(?:UTF-8'')?("?)([^";]+)\1/i);
  if (!match?.[2]) return fallback;
  try {
    return decodeURIComponent(match[2]);
  } catch {
    return match[2];
  }
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const DeveloperReportsService = {
  getProjectReport(projectId: number | string) {
    return http<DeveloperProjectReport>(`/developer/projects/${projectId}/report`);
  },

  async downloadReport(params: { format: 'pdf' | 'csv'; projectId?: number | string }) {
    const sp = new URLSearchParams({ format: params.format });
    if (params.projectId) sp.set('projectId', String(params.projectId));

    const res = await fetch(`${ENV.API_URL}/developer/reports/download?${sp.toString()}`, {
      method: 'GET',
      headers: {
        ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        Accept: params.format === 'csv' ? 'text/csv' : 'application/pdf',
      },
    });

    if (!res.ok) {
      const contentType = res.headers.get('content-type') || '';
      const payload = contentType.includes('application/json')
        ? await res.json().catch(() => null)
        : await res.text().catch(() => '');
      const message =
        (typeof payload === 'string' && payload.trim()) ||
        (payload && typeof payload.message === 'string' ? payload.message : '') ||
        'No se pudo descargar el reporte.';
      throw new Error(message);
    }

    const blob = await res.blob();
    const filename = deriveFilenameFromHeaders(
      res.headers,
      `reporte-desarrollador.${params.format === 'csv' ? 'csv' : 'pdf'}`,
    );
    downloadBlob(blob, filename);
  }
};
